import {
  BottomSheet,
  CardWithIntent,
  CbhIcon,
  DialogFooter,
  Illustration,
  KeyValueList,
  KeyValueListItem,
  Text,
  TextButton,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { PasswordDisplay } from "@src/appV2/redesign/Charting/PasswordDisplay";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { type ChartingCredential } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { Link } from "react-router-dom";

export interface ChartingCredentialsSetUpBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  workplaceId: string;
  workerId: string;
  chartingCredentials?: ChartingCredential;
  chartingInstructions?: string;
  onLoggedIn?: () => void;
  onTroubleLoggingIn?: () => void;
  onSkip?: () => void;
  onClose?: () => void;
}

export function ChartingCredentialsSetUpBottomSheet({
  modalState,
  shiftId,
  workplaceId,
  workerId,
  chartingCredentials,
  chartingInstructions,
  onLoggedIn,
  onTroubleLoggingIn,
  onSkip,
  onClose,
}: ChartingCredentialsSetUpBottomSheetProps) {
  const logMetadata = {
    shiftId,
    workplaceId,
    workerId,
  };

  const hasExistingCredentials =
    isDefined(chartingCredentials) &&
    isDefined(chartingCredentials.username) &&
    isDefined(chartingCredentials.password);

  const handleLoggedIn = () => {
    const eventName = hasExistingCredentials
      ? APP_V2_USER_EVENTS.CHARTING_CREDENTIALS_SETUP_LOGGED_IN_WITH_EXISTING_CREDENTIALS
      : APP_V2_USER_EVENTS.CHARTING_CREDENTIALS_SETUP_LOGGED_IN_WITHOUT_EXISTING_CREDENTIALS;
    logEvent(eventName, logMetadata);
    onLoggedIn?.();
    modalState.closeModal();
    onClose?.();
  };

  const handleTroubleLoggingIn = () => {
    const eventName = hasExistingCredentials
      ? APP_V2_USER_EVENTS.CHARTING_CREDENTIALS_SETUP_TROUBLE_LOGGING_IN_WITH_EXISTING_CREDENTIALS
      : APP_V2_USER_EVENTS.CHARTING_CREDENTIALS_SETUP_TROUBLE_LOGGING_IN_WITHOUT_EXISTING_CREDENTIALS;
    logEvent(eventName, logMetadata);
    onTroubleLoggingIn?.();
    modalState.closeModal();
    onClose?.();
  };

  const handleClose = () => {
    const eventName = hasExistingCredentials
      ? APP_V2_USER_EVENTS.CHARTING_CREDENTIALS_SETUP_SKIP_WITH_EXISTING_CREDENTIALS
      : APP_V2_USER_EVENTS.CHARTING_CREDENTIALS_SETUP_SKIP_WITHOUT_EXISTING_CREDENTIALS;
    logEvent(eventName, logMetadata);
    onSkip?.();
    modalState.closeModal();
    onClose?.();
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <BottomSheet
        canDismiss
        modalState={modalState}
        header={<BottomSheetHeader onClose={handleClose} />}
        onClose={handleClose}
      >
        <Stack spacing={6} sx={{ paddingX: 5 }}>
          <Stack spacing={3}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Illustration type="quiz-required" />
            </Box>
            <Text bold variant="h3" sx={{ textAlign: "center" }}>
              Get set up to chart
            </Text>
          </Stack>
          <Text variant="body1" sx={{ paddingX: 2, textAlign: "center", textWrap: "balance" }}>
            Charting is required for your shift. Please make sure you have access to the EHR.
          </Text>
          <Stack spacing={7}>
            {isDefined(chartingCredentials) &&
              isDefined(chartingCredentials.username) &&
              isDefined(chartingCredentials.password) && (
                <KeyValueList>
                  <KeyValueListItem
                    key="username"
                    label="Username"
                    value={chartingCredentials.username}
                  />
                  <KeyValueListItem
                    key="password"
                    label="Password"
                    value={<PasswordDisplay password={chartingCredentials.password} />}
                  />
                </KeyValueList>
              )}

            {isDefined(chartingInstructions) && (
              <CardWithIntent
                intent="neutral"
                sx={{
                  backgroundColor: (theme) => theme.palette.intent?.neutral.background,
                  color: (theme) => theme.palette.intent?.neutral.text,
                  padding: 4,
                }}
              >
                <Stack direction="row" spacing={3} alignItems="top" sx={{ padding: 1 }}>
                  <CbhIcon type="software" size="small" />
                  <Text variant="body2">{chartingInstructions}</Text>
                </Stack>
              </CardWithIntent>
            )}
          </Stack>
        </Stack>
        <DialogFooter sx={{ marginTop: 4 }} orientation="vertical" LinkComponent={Link}>
          <Button fullWidth size="large" onClick={handleLoggedIn}>
            I logged in
          </Button>

          <Button fullWidth size="large" variant="outlined" onClick={handleTroubleLoggingIn}>
            I have trouble logging in
          </Button>

          <TextButton size="large" LinkComponent={Link} onClick={handleClose}>
            Skip for now
          </TextButton>
        </DialogFooter>
      </BottomSheet>
    </ThemeProvider>
  );
}
